import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api } from '../lib/api'
import { Button } from '../components/ui/Button'
import { CardGridSkeleton } from '../components/ui/Skeletons'
import { Breadcrumb } from '../components/ui/Breadcrumb'
import { EntityHeader } from '../components/ui/EntityHeader'
import { SiteOverview } from '../components/site/SiteOverview'
import { SiteDevicesTab } from '../components/site/SiteDevicesTab'
import { SiteFloorplansTab } from '../components/site/SiteFloorplansTab'
import { SitePhotosTab } from '../components/site/SitePhotosTab'
import { SiteDocumentsTab } from '../components/site/SiteDocumentsTab'
import { SiteLicensesTab } from '../components/site/SiteLicensesTab'
import { SiteRemoteAccessTab } from '../components/site/SiteRemoteAccessTab'
import { DiscoveredDevicesPanel } from '../components/remote/DiscoveredDevicesPanel'
import { DeviceDetailPanel } from '../components/site/DeviceDetailPanel'
import { NetworkTopology } from '../components/site/NetworkTopology'
import { SiteProvisioningTab } from '../components/provisioning/SiteProvisioningTab'
import { SiteNetworkView } from '../components/site/SiteNetworkView'
import { SiteClientsTab } from '../components/site/SiteClientsTab'
import { SiteRackView } from '../components/site/SiteRackView'
import { AsBuiltExportModal } from '../components/site/AsBuiltExportModal'
import { NetworkDeviceModal } from '../components/site/NetworkDeviceModal'
import { useToast } from '../components/ui/Toast'
import type { ClientSite } from '@shared/types'

const SITE_NAV = [
  { group: 'SYSTEM', items: [
    { id: 'overview', label: 'Overview' },
    { id: 'devices', label: 'Devices' },
    { id: 'network', label: 'Network' },
    { id: 'racks', label: 'Racks' },
    { id: 'clients', label: 'Clients' },
    { id: 'topology', label: 'Topology' },
  ]},
  { group: 'PLANS', items: [
    { id: 'floorplans', label: 'Floor Plans' },
  ]},
  { group: 'SERVICE', items: [
    { id: 'workorders', label: 'Work Orders' },
    { id: 'licenses', label: 'Licenses' },
  ]},
  { group: 'ACCESS', items: [
    { id: 'remote', label: 'Remote Access' },
    { id: 'discovered', label: 'Discovered Devices' },
    { id: 'provisioning', label: 'Provisioning' },
  ]},
  { group: 'DOCS', items: [
    { id: 'photos', label: 'Photos' },
    { id: 'documents', label: 'Documents' },
    { id: 'asbuilt', label: 'As-Built' },
  ]},
] as const

type SectionId = typeof SITE_NAV[number]['items'][number]['id']

interface SiteWithStats extends ClientSite {
  client_name?: string
  stats: {
    device_counts: { category: string; count: number }[]
    total_devices: number
    plan_count: number
    rack_count: number
  }
}

function MapPinIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
}
function MonitorIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
}
function LayoutIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
}
function ServerIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>
}

export default function SiteDetail() {
  const { slug } = useParams<{ slug: string }>()
  const [site, setSite] = useState<SiteWithStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [section, setSection] = useState<SectionId>('overview')
  const [deviceFilter, setDeviceFilter] = useState<string | undefined>(undefined)
  const [detailDeviceSlug, setDetailDeviceSlug] = useState<string | null>(null)
  const [networkModal, setNetworkModal] = useState<{ deviceId: string; highlightPort?: number } | null>(null)
  const [showAsBuilt, setShowAsBuilt] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [unifiPicker, setUnifiPicker] = useState<{ sites: Array<{ site_id: string; site_name: string; host_name?: string }> } | null>(null)
  const [unifiSearch, setUnifiSearch] = useState('')
  const pickerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()

  // Click-outside to close picker
  useEffect(() => {
    if (!unifiPicker) return
    function handleClickOutside(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setUnifiPicker(null)
        setUnifiSearch('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [unifiPicker])

  // Auto-focus search when picker opens
  useEffect(() => {
    if (unifiPicker) setTimeout(() => searchInputRef.current?.focus(), 50)
  }, [unifiPicker])

  function navigateToDevices(filter?: string) {
    setDeviceFilter(filter)
    setSection('devices')
  }

  function openNetworkModal(deviceId: string, highlightPort?: number) {
    setNetworkModal({ deviceId, highlightPort })
  }

  async function handleUniFiSync(unifiSiteId?: string) {
    setSyncing(true)
    setUnifiPicker(null)
    setUnifiSearch('')
    try {
      const payload: { portalSiteId: string | undefined; unifiSiteId?: string } = { portalSiteId: site?.id }
      if (unifiSiteId) payload.unifiSiteId = unifiSiteId
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any = await api.post('/integrations/unifi/sync', payload)
      if (response.needsMapping && response.availableSites?.length > 0) {
        setUnifiPicker({ sites: response.availableSites })
      } else {
        loadSite()
        const total = (response.created || 0) + (response.updated || 0)
        if (total > 0) {
          // Build device type breakdown string
          const types = response.device_types as Record<string, number> | undefined
          let typeStr = ''
          if (types) {
            const parts = Object.entries(types)
              .filter(([k]) => k !== 'unknown')
              .map(([k, v]) => `${v} ${k.replace('_', ' ')}${v !== 1 ? 's' : ''}`)
            if (parts.length > 0) typeStr = ` (${parts.join(', ')})`
          }
          toast(`Synced ${total} device${total !== 1 ? 's' : ''}${typeStr}`)
        } else if (response.errors?.length > 0) {
          toast(response.errors[0], 'error')
        } else {
          toast('No devices found — check UniFi site mapping', 'info')
        }
      }
    } catch (err: unknown) {
      console.error('UniFi sync failed:', err)
      toast(err instanceof Error ? err.message : 'UniFi sync failed', 'error')
    } finally { setSyncing(false) }
  }

  const loadSite = useCallback(() => {
    if (!slug) return
    api.get<SiteWithStats>(`/sites/${slug}`)
      .then(setSite)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [slug])

  useEffect(() => { loadSite() }, [loadSite])

  if (loading) return <CardGridSkeleton cards={4} />
  if (!site) return <div style={{ padding: 24, color: 'var(--text-secondary)', fontSize: 14 }}>Site not found.</div>

  const location = [site.address, site.city, site.state].filter(Boolean).join(', ')
  const deviceCounts = site.stats?.device_counts ?? []
  const summaryCards = buildSummaryCards(deviceCounts)

  function renderContent() {
    switch (section) {
      case 'overview':
        return <SiteOverview site={site!} onOpenDevice={setDetailDeviceSlug} onNavigateDevices={navigateToDevices} />
      case 'devices':
        return <SiteDevicesTab siteId={site!.id} onOpenDevice={setDetailDeviceSlug} onOpenNetworkDevice={openNetworkModal} initialFilter={deviceFilter} />
      case 'network':
        return <SiteNetworkView siteId={site!.id} onOpenDevice={setDetailDeviceSlug} />
      case 'clients':
        return <SiteClientsTab siteId={site!.id} onOpenNetworkDevice={openNetworkModal} />
      case 'racks':
        return <SiteRackView siteId={site!.id} onSwitchToNetwork={() => setSection('network')} />
      case 'topology':
        return (
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Network Topology</h3>
            <NetworkTopology siteId={site!.id} onSelectDevice={(deviceId) => openNetworkModal(deviceId)} />
          </div>
        )
      case 'floorplans':
        return <SiteFloorplansTab siteId={site!.id} />
      case 'workorders':
        return <WorkOrdersPlaceholder siteName={site!.name} />
      case 'licenses':
        return <SiteLicensesTab siteId={site!.id} siteSlug={slug!} />
      case 'remote':
        return <SiteRemoteAccessTab siteId={site!.id} siteName={site!.name} onOpenDevice={setDetailDeviceSlug} />
      case 'discovered':
        return <DiscoveredDevicesPanel siteId={site!.id} />
      case 'provisioning':
        return <SiteProvisioningTab siteId={site!.id} />
      case 'photos':
        return <SitePhotosTab siteId={site!.id} />
      case 'documents':
        return <SiteDocumentsTab siteId={site!.id} />
      case 'asbuilt':
        return (
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 12 }}>As-Built Export</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>Generate an as-built documentation package for this site.</p>
            <Button
              onClick={() => setShowAsBuilt(true)}
            >
              Generate As-Built
            </Button>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className="page-enter" style={{ marginRight: detailDeviceSlug ? 420 : 0, transition: 'margin 0.25s ease' }}>
      <style>{CSS}</style>

      {/* Sticky site header */}
      <div style={{
        position: 'sticky', top: -24, zIndex: 10,
        background: 'var(--bg-0)',
        paddingTop: 24,
        paddingBottom: 12,
        marginTop: -24,
        marginLeft: -24, marginRight: -24, paddingLeft: 24, paddingRight: 24,
        borderBottom: '1px solid var(--border)',
      }}>
        <Breadcrumb items={[
          { label: 'Sites', to: '/sites' },
          ...(site.client_name ? [{ label: site.client_name, to: '/clients' }] : []),
          { label: site.name },
        ]} />

        <div style={{ position: 'relative' }}>
          <EntityHeader
          icon={MapPinIcon}
          title={site.name}
          badges={[
            { label: 'Active', variant: 'ok' as const, dot: true },
          ]}
          metrics={[
            { icon: MonitorIcon, label: 'Devices', value: site.stats?.total_devices ?? 0, onClick: () => setSection('devices') },
            { icon: LayoutIcon, label: 'Floor Plans', value: site.stats?.plan_count ?? 0, onClick: () => setSection('floorplans') },
            { icon: ServerIcon, label: 'Racks', value: site.stats?.rack_count ?? 0, onClick: () => setSection('racks') },
          ]}
          actions={[
            { label: syncing ? 'Syncing...' : 'Sync UniFi', onClick: () => handleUniFiSync(), variant: 'ghost' as const },
          ]}
        />
        {unifiPicker && (
          <div ref={pickerRef} style={{
            position: 'absolute', top: 48, right: 0, zIndex: 9999,
            background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8,
            boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.15))', padding: 6, minWidth: 300,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', padding: '4px 8px', marginBottom: 4 }}>
              Select UniFi site to link
            </div>
            <input
              ref={searchInputRef}
              autoFocus
              value={unifiSearch}
              onChange={e => setUnifiSearch(e.target.value)}
              placeholder="Search UniFi sites..."
              className="input"
              style={{ fontSize: 11, padding: '6px 8px', marginBottom: 4, width: '100%', boxSizing: 'border-box' }}
              onKeyDown={e => { if (e.key === 'Escape') { setUnifiPicker(null); setUnifiSearch('') } }}
            />
            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
              {(() => {
                const q = unifiSearch.toLowerCase().trim()
                const filtered = q
                  ? unifiPicker.sites.filter(s =>
                      s.site_name.toLowerCase().includes(q) ||
                      (s.host_name && s.host_name.toLowerCase().includes(q)) ||
                      s.site_id.toLowerCase().includes(q)
                    )
                  : unifiPicker.sites
                if (filtered.length === 0) {
                  return (
                    <div style={{ padding: 8, fontSize: 11, color: 'var(--text-tertiary)', textAlign: 'center' }}>
                      No matching sites
                    </div>
                  )
                }
                return filtered.map(s => (
                  <button
                    key={s.site_id}
                    onClick={() => handleUniFiSync(s.site_id)}
                    style={{
                      display: 'flex', flexDirection: 'column', width: '100%', textAlign: 'left', padding: '6px 8px',
                      background: 'transparent', border: 'none', borderRadius: 4, cursor: 'pointer',
                      fontFamily: 'var(--font-sans)',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-overlay)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{s.site_name}</span>
                    {s.host_name && <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{s.host_name}</span>}
                  </button>
                ))
              })()}
            </div>
          </div>
        )}
      </div>
      </div>{/* end sticky header */}

      {/* Two-column layout: grouped nav + content */}
      <div style={{ display: 'flex', gap: 0 }}>
        {/* Grouped sidebar nav */}
        <div className="site-nav">
          {SITE_NAV.map(group => (
            <div key={group.group} style={{ marginBottom: 12 }}>
              <div className="site-nav-group">{group.group}</div>
              {group.items.map(item => (
                <button
                  key={item.id}
                  onClick={() => { setDeviceFilter(undefined); setSection(item.id) }}
                  className={`site-nav-item ${section === item.id ? 'active' : ''}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Content area */}
        <div style={{ flex: 1, minWidth: 0, paddingLeft: 16 }}>
          <div className="page-enter" key={section}>
            {renderContent()}
          </div>
        </div>
      </div>

      {/* Device Detail Panel */}
      {detailDeviceSlug && (
        <DeviceDetailPanel
          deviceSlug={detailDeviceSlug}
          onClose={() => setDetailDeviceSlug(null)}
        />
      )}

      {/* Network Device Modal */}
      {networkModal && (
        <NetworkDeviceModal
          siteId={site.id}
          deviceId={networkModal.deviceId}
          highlightPort={networkModal.highlightPort}
          onClose={() => setNetworkModal(null)}
          onOpenDevice={setDetailDeviceSlug}
        />
      )}

      {/* As-Built Modal */}
      <AsBuiltExportModal
        isOpen={showAsBuilt}
        siteId={site.id}
        siteName={site.name}
        onClose={() => setShowAsBuilt(false)}
      />
    </div>
  )
}

function WorkOrdersPlaceholder({ siteName }: { siteName: string }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Work Orders</h3>
        <Link to="/dispatch" className="v3-btn v3-btn-primary v3-btn-sm" style={{ textDecoration: 'none' }}>
          View All in Dispatch
        </Link>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
        Work orders for {siteName} appear in the Dispatch board.
      </p>
    </div>
  )
}

function buildSummaryCards(counts: { category: string; count: number }[]) {
  const mapping: { categories: string[]; filterCategories: string[]; label: string; color: string }[] = [
    { categories: ['camera', 'surveillance'], filterCategories: ['camera'], label: 'Cameras', color: 'var(--color-camera)' },
    { categories: ['door', 'access', 'access_control'], filterCategories: ['controller'], label: 'Doors', color: 'var(--color-access)' },
    { categories: ['switch', 'networking'], filterCategories: ['switch'], label: 'Switches', color: 'var(--color-networking)' },
    { categories: ['nvr'], filterCategories: ['nvr'], label: 'NVR', color: 'var(--color-infra)' },
  ]
  return mapping.map(m => {
    const total = counts.filter(c => m.categories.includes(c.category)).reduce((s, c) => s + c.count, 0)
    return { label: m.label, value: total, color: m.color, filterCategories: m.filterCategories }
  }).filter(c => c.value > 0)
}

const CSS = `
  .site-nav {
    width: 160px;
    flex-shrink: 0;
    border-right: 1px solid var(--border-subtle);
    padding-right: 12px;
    position: sticky;
    top: 0;
    align-self: flex-start;
    max-height: calc(100vh - 52px);
    overflow-y: auto;
  }
  .site-nav-group {
    font-size: 9px;
    font-weight: 800;
    color: var(--text-disabled);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    padding: 6px 10px;
    margin-top: 8px;
  }
  .site-nav-item {
    display: block;
    width: 100%;
    padding: 6px 10px;
    border-radius: 6px;
    border: none;
    background: transparent;
    color: var(--text-tertiary);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    text-align: left;
    transition: all 0.1s;
    font-family: var(--font-sans);
  }
  .site-nav-item:hover {
    background: var(--bg-overlay);
    color: var(--text-secondary);
  }
  .site-nav-item.active {
    background: var(--accent-muted);
    color: var(--accent);
    font-weight: 600;
  }
  .page-enter {
    animation: pageIn 0.35s cubic-bezier(0.16,1,0.3,1);
  }
  @keyframes pageIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @media (max-width: 768px) {
    .site-nav {
      display: none;
    }
  }
`
